import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { npsApi } from '../api/npsApi';

const NPS_DISMISS_KEY = 'janus-nps-dismissed-at';
const NPS_SUBMITTED_KEY = 'janus-nps-submitted-at';
const DISMISS_DURATION_DAYS = 90;
const SUBMIT_COOLDOWN_DAYS = 180; // NPS suppression: don't show again for 180 days after response
const TENANT_ACTIVATION_MIN_DAYS = 30;

function daysSince(dateStr: string | null): number {
  if (!dateStr) return Infinity;
  const then = new Date(dateStr).getTime();
  return Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
}

export function useNpsSurvey() {
  const { user, isAuthenticated } = useAuth();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function checkSurvey() {
      // Don't track sessions if user is not authenticated or token is missing
      if (!isAuthenticated || !user || !localStorage.getItem('accessToken')) {
        setLoading(false);
        return;
      }

      // Small delay to ensure token is available after login redirect
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check local dismiss/submit cooldowns first
      const dismissedAt = localStorage.getItem(NPS_DISMISS_KEY);
      if (daysSince(dismissedAt) < DISMISS_DURATION_DAYS) {
        setLoading(false);
        return;
      }

      const submittedAt = localStorage.getItem(NPS_SUBMITTED_KEY);
      if (daysSince(submittedAt) < SUBMIT_COOLDOWN_DAYS) {
        setLoading(false);
        return;
      }

      // Don't show NPS if tenant was activated less than 30 days ago
      if (user?.tenantActivatedAt && daysSince(user.tenantActivatedAt) < TENANT_ACTIVATION_MIN_DAYS) {
        setLoading(false);
        return;
      }

      try {
        // Track session for server-side eligibility checks
        await npsApi.trackSession();

        // Check server-side survey status
        const status = await npsApi.getSurveyStatus();
        if (!cancelled && status.shouldShow) {
          setVisible(true);
        }
      } catch {
        // Silently fail — NPS should never break the app
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    checkSurvey();
    return () => {
      cancelled = true;
    };
  }, [user, isAuthenticated]);

  const dismiss = useCallback(async () => {
    setVisible(false);
    localStorage.setItem(NPS_DISMISS_KEY, new Date().toISOString());
    try {
      await npsApi.dismissSurvey();
    } catch {
      // Silently fail
    }
  }, []);

  const submit = useCallback(async (score: number, comment?: string) => {
    await npsApi.submitNps(score, comment);
    setVisible(false);
    localStorage.setItem(NPS_SUBMITTED_KEY, new Date().toISOString());
  }, []);

  return { visible, loading, dismiss, submit };
}
